import {
    useAddTcoRuleDialog,
    useDeleteTcoRuleDialog,
    useEditTcoRuleDialog,
} from '@/modules/SourcingTemplates/components/TocRulesDialog/TcoRuleDialog';
import {
    TotalCostOfOwnershipRule,
    formatConditions,
    useTotalCostOfOwnershipRules,
} from '@/modules/SourcingTemplates/components/TocRulesDialog/type';

import { Trans, t } from '@lingui/macro';
import {
    formatDecimal,
    formatLongDateTime,
    formatMonetaryValue,
    formatRelativeTime,
    transEnum,
} from '@luminovo/commons';
import {
    DestructiveSecondaryIconButton,
    Flexbox,
    Message,
    NonIdealState,
    PrimaryButton,
    SecondaryIconButton,
    Tag,
    TanStackTable,
    Text,
    Tooltip,
    colorSystem,
    createColumnHelper,
    useTanStackTable,
} from '@luminovo/design-system';
import { TotalCostOfOwnershipRuleCategory, TotalCostOfOwnershipRuleConditionType } from '@luminovo/http-client';
import {
    TotaCostOfOwnershipRuleCategoryTag,
    totalCostOfOwnershipRuleCategoryTranslations,
    totalCostOfOwnershipRuleConditionTypeTranslations,
} from '@luminovo/sourcing-core';
import { Add, AddCardRounded, Delete, Edit } from '@mui/icons-material';
import { Box } from '@mui/material';
import {
    UseFieldArrayAppend,
    UseFieldArrayRemove,
    UseFieldArrayUpdate,
    useFieldArray,
    useFormContext,
    useWatch,
} from 'react-hook-form';
import { SourcingScenarioFormValues } from './types';

type SharedContext = {
    append: UseFieldArrayAppend<SourcingScenarioFormValues, 'totalCostOfOwnershipRules'>;
    update: UseFieldArrayUpdate<SourcingScenarioFormValues, 'totalCostOfOwnershipRules'>;
    remove: UseFieldArrayRemove;
};

const columnHelper = createColumnHelper<TotalCostOfOwnershipRule, SharedContext>();

const columns = [
    columnHelper.text('name', {
        label: () => t`Name`,
        size: 120,
        cell: (item) => item.getValue(),
    }),

    columnHelper.text('description', {
        label: () => t`Description`,
        size: 130,
        cell: (item) => item.getValue(),
    }),

    columnHelper.text((row) => formatConditions(row.inlinedConditions), {
        id: 'conditions_text',
        label: () => t`Conditions (expanded)`,
        size: 300,
        initialVisibility: false,
    }),

    columnHelper.array((row) => row.inlinedConditions.map((c) => c.condition_type), {
        id: 'conditions',
        label: () => t`Conditions`,
        size: 275,
        renderType: 'text',
        options: Object.values(TotalCostOfOwnershipRuleConditionType),
        getOptionLabel: (opt) => transEnum(opt, totalCostOfOwnershipRuleConditionTypeTranslations),
        cell: ({ row }) => formatConditions(row.original.inlinedConditions),
    }),

    columnHelper.text(
        (row) => {
            if (row.action.type === 'Absolute') {
                return formatMonetaryValue(row.action.value);
            }
            if (row.action.type === 'Relative') {
                return formatDecimal(Number(row.action.value), { suffix: '%' });
            }
        },
        {
            id: 'action',
            label: () => t`Cost`,
            size: 200,
            align: 'right',
        },
    ),

    columnHelper.enum('category', {
        label: () => t`Category`,
        size: 200,
        options: Object.values(TotalCostOfOwnershipRuleCategory),
        getOptionLabel: (opt) => transEnum(opt, totalCostOfOwnershipRuleCategoryTranslations),
        cell: ({ row }) => <TotaCostOfOwnershipRuleCategoryTag category={row.original.category} />,
    }),

    columnHelper.enum('is_valid', {
        label: () => t`Valid`,
        size: 120,
        options: [true, false],
        getOptionLabel: (opt) => (opt ? t`Valid` : t`Invalid`),
        cell: ({ row }) =>
            row.original.is_valid ? (
                <Tag attention="low" color="green" label={t`Valid`} />
            ) : (
                <Tag attention="low" color="red" label={t`Invalid`} />
            ),
    }),

    columnHelper.date('updated_at', {
        label: () => t`Updated at`,
        size: 120,
        cell: (item) => (
            <Tooltip title={formatLongDateTime(item.getValue())}>
                <Text variant="body-small" color={colorSystem.neutral[8]}>
                    {formatRelativeTime(item.getValue())}
                </Text>
            </Tooltip>
        ),
    }),

    columnHelper.action({
        id: 'actions',
        size: 90,
        cell: function Cell({ row, sharedContext }) {
            const { openDialog: openEditDialog } = useEditTcoRuleDialog(row.original);
            const { openDialog: openDeleteDialog } = useDeleteTcoRuleDialog();
            return (
                <Flexbox gap={12} justifyContent={'center'}>
                    <Tooltip title={t`Edit rule`}>
                        <SecondaryIconButton
                            onClick={() => openEditDialog((values) => sharedContext.update(row.index, values))}
                            size="small"
                        >
                            <Edit fontSize="inherit" />
                        </SecondaryIconButton>
                    </Tooltip>
                    <Tooltip title={t`Remove rule`}>
                        <DestructiveSecondaryIconButton
                            onClick={() =>
                                openDeleteDialog(() => {
                                    sharedContext.remove(row.index);
                                })
                            }
                            size="small"
                        >
                            <Delete fontSize="inherit" />
                        </DestructiveSecondaryIconButton>
                    </Tooltip>
                </Flexbox>
            );
        },
    }),
];

function EmptyPlaceholder(): JSX.Element {
    return (
        <NonIdealState
            Icon={AddCardRounded}
            title={t`No total cost of ownership rules added yet`}
            description={t`Add your first total cost of ownership rule to get started`}
            style={{ maxWidth: '400px' }}
        />
    );
}

function ActionButton({ sharedContext }: { sharedContext: SharedContext }) {
    const { openDialog } = useAddTcoRuleDialog();

    return (
        <PrimaryButton size="medium" onClick={() => openDialog(sharedContext.append)} startIcon={<Add />}>
            <Trans>Add rule</Trans>
        </PrimaryButton>
    );
}

export function TotalCostOfOwnershipRulesTab() {
    const { control } = useFormContext<SourcingScenarioFormValues>();
    const isTcoEnabled = useWatch({ control, name: 'solutionPreferences.isTcoEnabled' });
    const { fields, append, update, remove } = useFieldArray({ control, name: 'totalCostOfOwnershipRules' });
    const { data } = useTotalCostOfOwnershipRules(fields);

    const { table } = useTanStackTable({
        data,
        columns,
        enableColumnHiding: true,
        enableColumnOrdering: true,
        sharedContext: {
            append,
            update,
            remove,
        },
    });

    return (
        <Flexbox flexDirection="column" gap={'12px'} height={'100%'}>
            {!isTcoEnabled && (
                <Message
                    attention="high"
                    variant="red"
                    size="small"
                    title={t`Total cost of ownership rules are disabled for this sourcing scenario, you can enable them in the solution preference tab`}
                />
            )}
            <Flexbox flexDirection="column" gap={4}>
                <Text>
                    <Trans>
                        The following costs and discounts are automatically added on top of the unit prices if their
                        condition is met.
                    </Trans>
                </Text>

                <Text variant="body-small">
                    <Flexbox gap={4} alignItems="baseline">
                        <Tag attention="low" color="neutral" label={t`TCO unit price`} />
                        = (
                        <Tag attention="low" color="neutral" label={t`Unit price`} />
                        +
                        <Tag attention="low" color="green" label={t`Packaging`} />
                        -
                        <Tag attention="low" color="violet" label={t`Discount`} />
                        ) +
                        <Tag attention="low" color="teal" label={t`Customs`} />
                        +
                        <Tag attention="low" color="yellow" label={t`Shipping`} />
                        +
                        <Tag attention="low" color="blue" label={t`Other`} />
                    </Flexbox>
                </Text>
            </Flexbox>

            <Box height="calc(100% - 48px)">
                <TanStackTable table={table} ActionButton={ActionButton} EmptyPlaceholder={EmptyPlaceholder} />
            </Box>
        </Flexbox>
    );
}
